import { Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import LoadingSpinner from '../../Components/LoadingSpinner';
import { useSelector } from "react-redux";
import { selectViewMenu } from "../../Selectors/ViewMenuSelectors";
import SubHeader from "../../Layouts/SubHeader";
import Header from "../../Layouts/Header";
import { getEntryAvailabilityTagsMap, sortEntries, useGameAvailability, useMyListEntries } from "../../Utils/Entry";
import { convertObjectIntoQueryString, generatePathCategoryEntry, generatePathEntry, getQueryParamsAndParse, mergeParamsIntoQueryString, removeParamsFromQueryString } from "../../Utils/Routes";
import Page from "../../Components/Page";
import LargeGridViewEntryList from "../../Components/Entry/List/GridViewEntryList";
import ListViewEntryList from "../../Components/Entry/List/ListViewEntryList";
import SummaryViewEntryList from "../../Components/Entry/List/SummaryViewEntryList";
import { useAwards } from "../../Utils/Award";
import EntryListRoot from "../../Components/Entry/List/EntryListRoot";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { EMPTY_FILTERS } from "../../Components/Filters/FilterDropdown";
import { useScrollToPositionId } from "../../Utils/ScrollToPosition";
import { useScrollToTop } from "../../Utils/ScrollToTop";
import { getGameCodesAvailability } from "../../Utils/Api";
import { expandCategoryPageEntries, toEntryData } from "../../Components/Entry/List/GenericEntryList";
import { filterEntries, useAwardEntries } from "../../Utils/Entry";

const useStyles = makeStyles((theme) => ({
    numEntries: {
        textAlign: "right",
        fontSize: `clamp(10px, 2vw, 14px)`,
        lineHeight: 1,
        margin: `0 ${theme.spacing(4)}`,
        padding: `0`,
        color: theme.palette.secondary.light,
    },
}));

function AwardPage () {
    const classes = useStyles();

    const navigate = useNavigate();
    const location = useLocation();
    const { search: queryString } = location;
    const urlParams = useParams();
    const { awardId } = urlParams;

    const awards = useAwards();
    const award = useMemo(() => awards?.find(award => award.awardId === parseInt(awardId)), [awards, awardId]);

    const categoryId = parseInt(urlParams?.categoryId) || null;

    const filterParams = useMemo(() => {
        const filterParams = getQueryParamsAndParse(queryString, {
            searchQuery: { type: "string" },
            categoriesSelected: { type: "array", subType: "int" },
            tagsSelected: { type: "array", subType: "string" },
            watched: { type: "string"},
            notes: { type: "string"},
            fixedCategories: { 
                type: "array", 
                subType: "int", 
                custom: (categories) => categories.filter(c => !isNaN(c)) 
            },
        }, {
            removeUnknown: true,
        });
        if(categoryId) {
            filterParams.fixedCategories = [categoryId];
        }
        return filterParams;
    }, [queryString, categoryId]);

    const isCategoryEntrySearch = Boolean(urlParams?.categoryId?.toLocaleLowerCase() === "all" && filterParams?.searchQuery?.trim()?.length);
    console.log("isCategoryEntrySearch", isCategoryEntrySearch);

    const [activeFilters, setActiveFilters] = useState(filterParams);
    const { viewType } = useSelector(selectViewMenu);
    const { sortType } = useSelector(selectViewMenu)
    const [searchQueryTextSearched, setSearchQueryTextSearched] = useState(filterParams.searchQuery);
    
    const backUrlParams = encodeURIComponent(convertObjectIntoQueryString({}, activeFilters));
    const backUrlQueryString = backUrlParams ? "bup=" + backUrlParams : "";

    const activeFiltersForFiltering = useMemo(() => ({
        ...activeFilters,
        searchQuery: searchQueryTextSearched,
        categoriesSelected: activeFilters.fixedCategories ? activeFilters.fixedCategories : activeFilters.categoriesSelected,
    }), [activeFilters, searchQueryTextSearched]);

    const initialGameAvailability = useGameAvailability(awardId);
    const [gameAvailabilityState, setGameAvailability] = useState(null);
    const gameAvailability = gameAvailabilityState ?? initialGameAvailability;
    console.log("gameAvailability", gameAvailability);

    const { additionalFilterTags, entryTagsMap } = useMemo(() => {
        return {
            additionalFilterTags: !gameAvailability?.platforms
                ? null 
                : Object.keys(gameAvailability.platforms)
                    .map(tagString => ({
                        id: tagString.toUpperCase(), 
                        title: tagString.replace(/,/g, ''),
                    })),
            entryTagsMap: getEntryAvailabilityTagsMap(gameAvailability?.platforms),
        };
    }, [gameAvailability]);
    console.log("additionalFilterTags", additionalFilterTags);
    console.log("entryTagsMap", entryTagsMap);

    const allEntries = useAwardEntries(award);
    console.log("allEntries", allEntries);

    const isFilteringAvailability = Boolean(
        award?.judgingMediaType === "game"
        && activeFiltersForFiltering?.tagsSelected
            ?.some(tag => additionalFilterTags?.some(tagObj => tagObj.title.toLocaleLowerCase() === tag.toLocaleLowerCase()))
    );
    console.log("isFilteringAvailability", isFilteringAvailability);


    const handler = useRef(null);
    useEffect(() => {
        console.log("getGameCodesAvailability useEffect");
        if(isFilteringAvailability && !handler.current) {
            console.log("getGameCodesAvailability starting setInterval");
            handler.current = setInterval(async () => {
                console.log("Refreshing getGameCodesAvailability...");
                const availablity = await getGameCodesAvailability(awardId);

                console.log("Testing if getGameCodesAvailability is different...", availablity);
                if(JSON.stringify(availablity) !== JSON.stringify(gameAvailability)) {
                    console.log("getGameCodesAvailability is different, updating state");
                    setGameAvailability(availablity);
                } else {
                    console.log("getGameCodesAvailability is the same, doing nothing");
                }
            }, 1000 * 5);
        }
        else {
            if(handler.current) {
                console.log("Clearing getGameCodesAvailability (else)");
                clearInterval(handler.current);
                handler.current = null;
            }
        }
        return () => {
            if(handler.current) {
                console.log("Clearing getGameCodesAvailability (return)");
                clearInterval(handler.current);
                handler.current = null;
            }
        };
    }, [awardId, gameAvailability, isFilteringAvailability]);
    
    const entries = useMemo(() => {
        if(!allEntries || !award) return null;

        const filteredEntries = filterEntries(allEntries, activeFiltersForFiltering, entryTagsMap);
        const sortedEntries = sortEntries(filteredEntries, sortType);
        return sortedEntries;
    }, [allEntries, award, activeFiltersForFiltering, entryTagsMap, sortType]);

    const myListEntries = useMyListEntries(awards);

    const fixedCategoryIds = useMemo(() => activeFilters?.fixedCategories ?? [], [activeFilters]);
    const fixedCategories = useMemo(() =>
        !award 
        ? null
        : fixedCategoryIds
            .map(id => award.categories[id])
            .filter(x => x)
    , [award, fixedCategoryIds]);

    const totalEntries = parseInt(
        fixedCategories?.length
            ? fixedCategories[0].numEntries
            : award?.numEntries
    )

    const totalVisible = entries?.length;

    useEffect(() => {
        if(award?.hasCategoryPage && !(filterParams?.fixedCategories?.length || isCategoryEntrySearch)) {
            navigate(award.urlNextTo, { replace: true });
        }
    }, [navigate, award, filterParams?.fixedCategories?.length, isCategoryEntrySearch]);

    const handleFilterChange = useCallback((filters) => {
        setActiveFilters(filters);

        const searchFilters = filters === EMPTY_FILTERS
            ? removeParamsFromQueryString(location.search, Object.keys(EMPTY_FILTERS))
            : mergeParamsIntoQueryString(location.search, filters)
        ;
        navigate({ search: searchFilters });
    }, [location.search, navigate]);

    const handleSearchSubmit = useCallback((text) => {
        if(text === (searchQueryTextSearched ?? "")) return;

        navigate({ 
            search: mergeParamsIntoQueryString(location.search, {
                searchQuery: text ? text : undefined,
            }),
        });
        setSearchQueryTextSearched(text);
    }, [searchQueryTextSearched, navigate, location.search]);

    const title = [
            fixedCategories && fixedCategories.length
                ? fixedCategories[0].title
                : null,
            award 
                ? award.title
                : "My List"
        ]
        .filter(x => x)
        .join(" | ");

    const isLoading = !entries || !awards;

    const entryListLinkComponent = memo(useCallback(function EntryListLinkComponent({ entry, entryCategoryId = null, ...props }) {

        const awardId = entry.awardId;

        const linkCategoryId = entryCategoryId ?? categoryId;

        const baseEntryLink = linkCategoryId
            ? generatePathCategoryEntry(awardId, linkCategoryId, entry.id)
            : generatePathEntry(awardId, entry.id)

        const entryLink = baseEntryLink + (backUrlQueryString ? "?" + backUrlQueryString : "");

        return <Link to={entryLink} {...props} />;
    }, [backUrlQueryString, categoryId]));

    const excludeGridView = award && award.hasCategoryPage && award.judgingMediaType !== "game";
    const displayViewType = excludeGridView && viewType === 'largeView' ? 'summaryView' : viewType;

    const ListComponent = {
        summaryView: SummaryViewEntryList,
        largeView: LargeGridViewEntryList,
        listView: ListViewEntryList,
    }[displayViewType] ?? SummaryViewEntryList;
    console.log("ListComponent", ListComponent);

    useScrollToTop();
    useScrollToPositionId(!isLoading);

    const entriesData = isCategoryEntrySearch ? expandCategoryPageEntries(entries) : toEntryData(entries);
    // const entriesData = filterMyListEntryData(categoriesEntriesData);
    console.log("entriesData", entriesData);
    
    return (
        <Page title={title}>
            { isLoading ? (
                <LoadingSpinner />
            ) : (
                <>
                    <Header />
                    <SubHeader
                        currentAward={award}
                        currentFixedCategories={fixedCategories}
                        entries={allEntries} 
                        onFilterChange={handleFilterChange}
                        activeFilters={activeFilters}
                        additionalFilterTags={additionalFilterTags}
                        backUrl={award && award.hasCategoryPage ? award.urlNextTo : "/"}
                        showMyListInMenu={Boolean(myListEntries?.length)}
                        searchQueryText={searchQueryTextSearched}
                        onSearchSubmit={handleSearchSubmit}
                    />

                    <div style={{marginTop: "1rem"}}>
                        <Typography variant="button" className={["upper", classes.numEntries].join(" ")} component="div">
                            {`${totalVisible} ${totalEntries > totalVisible ? `of ${totalEntries}`:''} entries`}
                        </Typography>

                        <EntryListRoot justifyContent="center">
                            <ListComponent
                                entriesData={entriesData}
                                linkComponent={entryListLinkComponent}
                            />
                        </EntryListRoot>
                    </div>
                </>
            )}
        </Page>
    );
}

export default AwardPage;